import { CursorClick01, RefreshCw01 } from "@untitledui/icons";
import { useState } from "react";
import { type AdsAuthStatus, useAdminAds, useRefreshAdsStatus } from "@/api/admin/ads";
import { usePagination } from "@/api/pagination";
import { Button } from "@/components/base/buttons/button";
import { useCache } from "@/hooks/use-cache";
import { useDebounce } from "@/hooks/use-debounce";
import { useSort } from "@/hooks/use-sort";
import { PageHeader } from "@/pages/admin/page-header";
import { AdminAdsTable } from "@/pages/admin/tables/ads";

export type CheckedStatus =
  | { ok: true; status: AdsAuthStatus; checkedAt: Date }
  | { ok: false; error: string; checkedAt: Date };

export const AdsPage = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const pagination = usePagination();
  const sort = useSort<"id" | "name" | "createdAt">("createdAt");
  const { data } = useAdminAds(pagination.pagination, sort.sort, { text: debouncedSearch });
  const total = useCache(data?.total);
  const refresh = useRefreshAdsStatus();
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  // Live auth/permission results, held for the session only (not persisted server-side).
  const [statuses, setStatuses] = useState<Map<string, CheckedStatus>>(new Map());

  const brands = data?.brands ?? [];

  // Refresh sequentially - each brand hits the TikTok Ads API, so we avoid firing
  // dozens of concurrent calls when "Refresh all" is used.
  const runRefresh = async (ids: string[]) => {
    for (const id of ids) {
      setRefreshingIds((prev) => new Set(prev).add(id));
      try {
        const status = await refresh.mutateAsync(id);
        setStatuses((prev) => new Map(prev).set(id, { ok: true, status, checkedAt: new Date() }));
      } catch (e) {
        // Record the failure so the row reads "Failed" (with the error) rather than
        // sitting on "Not checked" as if it had never run.
        const error = e instanceof Error ? e.message : "Check failed";
        setStatuses((prev) => new Map(prev).set(id, { ok: false, error, checkedAt: new Date() }));
      } finally {
        setRefreshingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  };

  return (
    <div className="h-full flex flex-col gap-6 mt-4 px-12">
      <PageHeader
        title="Ads"
        subtitle="TikTok Ads authorisation and GMV Max status for every merchant"
        icon={CursorClick01}
        count={total}
        search={search}
        onSearchChange={setSearch}
        actions={
          <Button
            color="secondary"
            iconLeading={RefreshCw01}
            isDisabled={refreshingIds.size > 0 || brands.length === 0}
            isLoading={refreshingIds.size > 0}
            onClick={() => runRefresh(brands.map((b) => b.merchantId))}
          >
            Refresh all
          </Button>
        }
      />
      <div className="flex-1 min-h-0 flex flex-col overflow-auto">
        <AdminAdsTable
          brands={brands}
          statuses={statuses}
          sort={sort}
          page={pagination.page}
          totalPages={Math.ceil((total ?? 0) / pagination.pagination.limit)}
          onPageChange={pagination.setPage}
          onRefresh={(id) => runRefresh([id])}
          refreshingIds={refreshingIds}
        />
      </div>
    </div>
  );
};
